// A promise wrapper over the handful of IndexedDB operations the cache needs.
//
// The tradeoff, named: the `idb` package is about 1.5KB and removes genuinely
// tedious request/event boilerplate. What we need is sixty lines of that
// boilerplate, so it doesn't earn its place.
//
// IndexedDB rather than localStorage because the lists can outgrow
// localStorage's ~5MB budget, and its writes block the main thread.

const DB_NAME = 'hewey'
const DB_VERSION = 1

export const RESOURCES = 'resources'
export const META = 'meta'

const promisify = <T>(req: IDBRequest<T>): Promise<T> =>
	new Promise((resolve, reject) => {
		req.addEventListener('success', () => resolve(req.result))
		req.addEventListener('error', () =>
			reject(req.error ?? new Error('IndexedDB request failed')),
		)
	})

let opening: Promise<IDBDatabase> | null = null

export const openDb = (): Promise<IDBDatabase> => {
	opening ??= new Promise((resolve, reject) => {
		if (typeof indexedDB === 'undefined') {
			reject(new Error('IndexedDB unavailable'))
			return
		}
		const req = indexedDB.open(DB_NAME, DB_VERSION)
		req.addEventListener('upgradeneeded', () => {
			const db = req.result
			for (const name of [RESOURCES, META]) {
				if (!db.objectStoreNames.contains(name)) {
					db.createObjectStore(name)
				}
			}
		})
		req.addEventListener('success', () => resolve(req.result))
		req.addEventListener('error', () =>
			reject(req.error ?? new Error('IndexedDB open failed')),
		)
		req.addEventListener('blocked', () =>
			reject(new Error('IndexedDB open blocked')),
		)
	})
	return opening
}

const store = async (name: string, mode: IDBTransactionMode) => {
	const db = await openDb()
	return db.transaction(name, mode).objectStore(name)
}

export const readOne = async (name: string, key: string): Promise<unknown> =>
	promisify((await store(name, 'readonly')).get(key))

export const readAll = async (name: string): Promise<Map<string, unknown>> => {
	const os = await store(name, 'readonly')
	// Both requests are issued before the first await, so the transaction
	// can't auto-commit between them.
	const [keys, values] = await Promise.all([
		promisify(os.getAllKeys()),
		promisify(os.getAll()),
	])
	const out = new Map<string, unknown>()
	keys.forEach((key, i) => out.set(String(key), values[i]))
	return out
}

export const write = async (name: string, key: string, value: unknown) => {
	await promisify((await store(name, 'readwrite')).put(value, key))
}

export const remove = async (name: string, key: string) => {
	await promisify((await store(name, 'readwrite')).delete(key))
}

export const clearStore = async (name: string) => {
	await promisify((await store(name, 'readwrite')).clear())
}
